import { assetName } from '@/components/assets/asset-presenter';
import type { Asset } from '@/lib/queries';
import {
  ArrowLeft,
  ArrowRight,
  Bell,
  BellOff,
  Bookmark,
  Calendar,
  Camera,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  CreditCard,
  Download,
  ExternalLink,
  Eye,
  EyeOff,
  Gift,
  Globe,
  Heart,
  Home,
  Info,
  Lock,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Minus,
  Play,
  Plus,
  Search,
  Send,
  Settings,
  Share2,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Tag,
  ThumbsUp,
  Trash2,
  TriangleAlert,
  Truck,
  User,
  Users,
  Wallet,
  X,
  Zap,
  type LucideIcon,
} from 'lucide-react';

// THE ICON LIBRARY. There is no `icon` block and there will not be one — SPEC §5.12 cut it as a
// COMPOSITION, and this file is that composition's authoring half: pick a glyph, get an `image`
// with a square `aspect_ratio` and a `tint`.
//
// CURATED, NOT COMPLETE. Lucide ships ~1,600 icons; a picker over all of them is a search box with
// no answer, and every one an author picks becomes an asset in their app. These are the glyphs an
// in-app experience actually uses — a paywall, a permission prompt, an order update, a nudge.
// Adding one is a line; the cost of the other 1,550 is paid by every author who scrolls past them.

/** The raster size every library icon is baked at. 24pt is the design size and 6× covers a 3×
 *  screen showing one at 48pt — past that an author should bring real artwork. Fixed, because it is
 *  half of the dedup key: two picks of `check` must resolve to ONE asset, not two. */
export const ICON_RASTER_PX = 144;

export interface CatalogIcon {
  /** Stable, kebab-case, and part of the asset filename — renaming one orphans every asset already
   *  minted from it, so treat these as permanent. */
  name: string;
  label: string;
  /** Extra search terms. An author looking for "buy" should find the shopping bag. */
  keywords: string;
  Icon: LucideIcon;
}

export const ICON_CATALOG: readonly CatalogIcon[] = [
  { name: 'check', label: 'Check', keywords: 'done tick yes confirm', Icon: Check },
  { name: 'check-circle', label: 'Check circle', keywords: 'done success complete', Icon: CheckCircle2 },
  { name: 'x', label: 'Close', keywords: 'cancel dismiss no remove', Icon: X },
  { name: 'plus', label: 'Plus', keywords: 'add new create', Icon: Plus },
  { name: 'minus', label: 'Minus', keywords: 'remove subtract less', Icon: Minus },
  { name: 'info', label: 'Info', keywords: 'about help details', Icon: Info },
  { name: 'alert', label: 'Alert', keywords: 'warning caution error danger', Icon: TriangleAlert },
  { name: 'star', label: 'Star', keywords: 'rate favourite favorite review', Icon: Star },
  { name: 'heart', label: 'Heart', keywords: 'like love favourite favorite save', Icon: Heart },
  { name: 'thumbs-up', label: 'Thumbs up', keywords: 'like approve feedback', Icon: ThumbsUp },
  { name: 'sparkles', label: 'Sparkles', keywords: 'new ai magic premium highlight', Icon: Sparkles },
  { name: 'zap', label: 'Zap', keywords: 'fast power boost instant', Icon: Zap },
  { name: 'gift', label: 'Gift', keywords: 'reward offer promo present', Icon: Gift },
  { name: 'tag', label: 'Tag', keywords: 'price discount label offer', Icon: Tag },
  { name: 'bell', label: 'Bell', keywords: 'notification alert push remind', Icon: Bell },
  { name: 'bell-off', label: 'Bell off', keywords: 'mute notification silence', Icon: BellOff },
  { name: 'bookmark', label: 'Bookmark', keywords: 'save later read', Icon: Bookmark },
  { name: 'lock', label: 'Lock', keywords: 'secure private premium paywall', Icon: Lock },
  { name: 'shield-check', label: 'Shield check', keywords: 'secure safe privacy trust', Icon: ShieldCheck },
  { name: 'eye', label: 'Eye', keywords: 'show view visible preview', Icon: Eye },
  { name: 'eye-off', label: 'Eye off', keywords: 'hide hidden private', Icon: EyeOff },
  { name: 'user', label: 'User', keywords: 'account profile person', Icon: User },
  { name: 'users', label: 'Users', keywords: 'team people group community', Icon: Users },
  { name: 'settings', label: 'Settings', keywords: 'preferences gear options config', Icon: Settings },
  { name: 'home', label: 'Home', keywords: 'house start main', Icon: Home },
  { name: 'menu', label: 'Menu', keywords: 'hamburger nav more', Icon: Menu },
  { name: 'search', label: 'Search', keywords: 'find magnify lookup', Icon: Search },
  { name: 'mail', label: 'Mail', keywords: 'email message inbox contact', Icon: Mail },
  { name: 'message', label: 'Message', keywords: 'chat comment support talk', Icon: MessageCircle },
  { name: 'send', label: 'Send', keywords: 'submit share paper plane', Icon: Send },
  { name: 'share', label: 'Share', keywords: 'invite refer social', Icon: Share2 },
  { name: 'external-link', label: 'External link', keywords: 'open browser url outbound', Icon: ExternalLink },
  { name: 'globe', label: 'Globe', keywords: 'world language region web', Icon: Globe },
  { name: 'map-pin', label: 'Map pin', keywords: 'location address place delivery', Icon: MapPin },
  { name: 'truck', label: 'Truck', keywords: 'delivery shipping order tracking', Icon: Truck },
  { name: 'shopping-bag', label: 'Shopping bag', keywords: 'buy purchase store checkout', Icon: ShoppingBag },
  { name: 'shopping-cart', label: 'Shopping cart', keywords: 'basket buy checkout', Icon: ShoppingCart },
  { name: 'credit-card', label: 'Credit card', keywords: 'pay payment billing subscribe', Icon: CreditCard },
  { name: 'wallet', label: 'Wallet', keywords: 'balance money pay funds', Icon: Wallet },
  { name: 'calendar', label: 'Calendar', keywords: 'date schedule booking event', Icon: Calendar },
  { name: 'clock', label: 'Clock', keywords: 'time reminder soon expiry', Icon: Clock },
  { name: 'camera', label: 'Camera', keywords: 'photo permission capture', Icon: Camera },
  { name: 'play', label: 'Play', keywords: 'video watch start media', Icon: Play },
  { name: 'download', label: 'Download', keywords: 'save export get', Icon: Download },
  { name: 'trash', label: 'Trash', keywords: 'delete remove bin', Icon: Trash2 },
  { name: 'arrow-right', label: 'Arrow right', keywords: 'next forward continue', Icon: ArrowRight },
  { name: 'arrow-left', label: 'Arrow left', keywords: 'back previous return', Icon: ArrowLeft },
  { name: 'chevron-right', label: 'Chevron right', keywords: 'next disclosure more', Icon: ChevronRight },
  { name: 'chevron-left', label: 'Chevron left', keywords: 'back previous', Icon: ChevronLeft },
  { name: 'chevron-up', label: 'Chevron up', keywords: 'collapse less', Icon: ChevronUp },
  { name: 'chevron-down', label: 'Chevron down', keywords: 'expand more dropdown', Icon: ChevronDown },
];

/** Rank the catalogue against a search box. An exact name match first, then a label prefix, then
 *  anything that mentions the term — so typing "check" puts Check above Shield check. */
export function searchIcons(query: string): readonly CatalogIcon[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return ICON_CATALOG;
  const score = (icon: CatalogIcon): number => {
    if (icon.name === needle) return 0;
    if (icon.label.toLowerCase().startsWith(needle)) return 1;
    if (icon.name.includes(needle)) return 2;
    if (`${icon.label} ${icon.keywords}`.toLowerCase().includes(needle)) return 3;
    return Infinity;
  };
  return ICON_CATALOG.map((icon) => ({ icon, rank: score(icon) }))
    .filter((entry) => entry.rank !== Infinity)
    .sort((a, b) => a.rank - b.rank || a.icon.label.localeCompare(b.icon.label))
    .map((entry) => entry.icon);
}

/** THE DEDUP KEY. An icon picked twice in one app must resolve to ONE asset — otherwise every pick
 *  uploads another near-identical PNG and the library fills with `check` copies. Encoded in the
 *  filename because that is the only field the asset API round-trips unchanged, and it carries the
 *  raster size so a future bump to `ICON_RASTER_PX` re-mints rather than silently reusing a
 *  lower-resolution glyph. */
export function iconFileName(name: string, px: number = ICON_RASTER_PX): string {
  return `icon-${name}-${px}.png`;
}

/** Is this asset one the picker minted? Drives the Assets panel's `Icons` filter. An icon is a
 *  perfectly ORDINARY managed asset — that is the whole point of rasterizing on pick, since it
 *  inherits revisions, usage tracking, Trash and purge for free — so its name is the only thing
 *  that distinguishes it. A tag would be truer, but tags are the author's to edit and a filter that
 *  an author can silently break by clearing a tag is a filter that lies. */
export function isIconAsset(asset: Asset): boolean {
  return asset.content_type === 'image/png' && ICON_FILE.test(assetName(asset));
}

const ICON_FILE = /^icon-.+-\d+\.png$/;

export function findIconAsset(
  assets: readonly Asset[],
  name: string,
  px: number = ICON_RASTER_PX
): Asset | undefined {
  const wanted = iconFileName(name, px);
  return assets.find((asset) => assetName(asset) === wanted);
}
